import { JSX } from 'react';
import { Box, Button, Typography } from '@mui/material';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { useDispatch } from 'react-redux';
import { interviewActions } from 'src/redux/slices/Interview/interviewSlice';
import { useIPE } from 'src/redux/slices/Interview/hooks';
import InterviewFooter from '../components/InterviewFooter';

export default function SkipGateScreen(): JSX.Element {
  const dispatch = useDispatch();
  const { callIPE } = useIPE();

  const handleAnswerNow = (): void => {
    dispatch(interviewActions.clearAllDeferred());
    dispatch(interviewActions.setLoading(true));
    dispatch(interviewActions.setWorkItemType('loading'));
    callIPE(); // Fresh IPE call with no skipped_qit_ids
  };

  const handleAnswerLater = (): void => {
    dispatch(interviewActions.setWorkItemType('complete'));
  };

  return (
    <Box
      data-testid="skip-gate-screen"
      sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}
    >
      {/* Body — vertically centered */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          flex: 1,
          gap: 2.5,
          px: 3,
          py: 5,
        }}
      >
        <Box
          sx={{
            width: { xs: 80, md: 88 },
            height: { xs: 80, md: 88 },
            borderRadius: '50%',
            bgcolor: 'warning.lighter',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ScheduleIcon
            sx={{ fontSize: { xs: 36, md: 40 }, color: 'warning.dark' }}
          />
        </Box>
        <Typography
          sx={{
            fontSize: { xs: 22, md: 24 },
            fontWeight: 700,
            lineHeight: 1.3,
            maxWidth: { xs: 300, md: 400 },
          }}
        >
          You still have some questions you skipped
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: 15, md: 16 },
            color: 'text.secondary',
            lineHeight: 1.5,
            maxWidth: { xs: 300, md: 400 },
          }}
        >
          Would you like to go back and answer them now, or finish later?
        </Typography>
        {/* Desktop-only inline action buttons */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            gap: 2,
            justifyContent: 'center',
            mt: 1,
          }}
        >
          <Button
            onClick={handleAnswerLater}
            data-testid="answer-later-button-inline"
            sx={{
              height: 44,
              borderRadius: '12px',
              border: '1px solid',
              borderColor: 'grey.300',
              color: 'text.secondary',
              fontSize: 14,
              fontWeight: 500,
              px: 2.5,
              textTransform: 'none',
            }}
          >
            I&apos;ll answer later
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAnswerNow}
            data-testid="answer-now-button-inline"
            sx={{
              height: 44,
              borderRadius: '12px',
              fontSize: 16,
              fontWeight: 600,
              px: 3,
              textTransform: 'none',
            }}
          >
            Answer now
          </Button>
        </Box>
      </Box>

      {/* Mobile-only footer */}
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        <InterviewFooter
          primaryLabel="Answer now"
          secondaryLabel="I'll answer later"
          onPrimary={handleAnswerNow}
          onSecondary={handleAnswerLater}
          primaryTestId="answer-now-button"
          secondaryTestId="answer-later-button"
        />
      </Box>
    </Box>
  );
}
